import React, { useEffect, useState } from 'react';
import { Breadcrumb, Menu } from 'antd';
import { FormattedMessage } from 'react-intl';
import styles from './styles';
import { RBHTable, RBHBox, RBHButton, RBHText, RBHTextInput, RBHEmpty, RBHError } from '../../../shared/components';
import { useHistory } from 'react-router';
import { httpCreators, useLoadingSelector } from '@ppvrbh/merchant-frontend-core';
import { useDispatch, useSelector } from 'react-redux';
import { USER_STATE_KEY, userStateSelector } from './redux/user-reducer';
import { USER_REQUEST_ID_LIST } from './redux/user-constant';
import { userSagas } from './redux/user-saga';
import { UserService } from './redux/user-service';
import { Colors, Fonts } from '../../../shared/theme';
import styleText from '../../../shared/components/rbh-text/styles';
import styleButton from '../../../shared/components/rbh-button/styles';
import styleInput from '../../../shared/components/rbh-textinput/styles';
import styleImage from '../../../shared/components/rbh-image/styles';
import { mainCreators } from '../../main-action';
import { MAIN_STATE_KEY } from '../../main-reducer';
import { MainHelper } from '../../main-helper';
import { EditOutlined, DeleteOutlined, TeamOutlined, SearchOutlined, FilterFilled } from '@ant-design/icons';
import { generalCreators } from '../../../redux/general/general-action';
import { GENERAL_STATE_KEY } from '../../../redux/general/general-reducer';
import { DEFAULT_PAGE_SIZE, MODAL_TYPE } from '../../../redux/general/general-constant';
import { FormAddEdit } from './components/FormAddEdit';
import metrics from '../../../shared/theme/metrics';
import { userCreators } from './redux/user-action';

export const UserManagementScreen: React.FC<Props> = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const data = useSelector(userStateSelector(USER_STATE_KEY.LIST)) as [];
  const loading = useLoadingSelector(USER_REQUEST_ID_LIST.GET_USER);
  const userStatusCode = MainHelper.getMasterDataByScheme('MerchantUserStatus');
  const [edit, setEdit] = useState(null);

  useEffect(() => {
    dispatch(mainCreators.setMainState(MAIN_STATE_KEY.HEADER_MENU, 2));
    reload();
  }, []);

  const resetReload = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setEdit(null);
    reload();
  };

  const reload = () => {
    dispatch(userCreators.callUser());
  };

  const pushToMain = () => {
    history.push('/main');
  };

  const pushToUser = () => {
    resetReload();
  };

  const addUser = () => {
    history.push('/main/adduser');
  };

  const onMenuItemClick = (setSelectedKeys: any, selectedKeys: any, confirm: any, clearFilters: any, value: any) => {
    if (selectedKeys[0] === value) {
      clearFilters();
    } else {
      setSelectedKeys([value]);
      confirm();
    }
  };

  const deleteUser = (merchantUserId: string) => {
    dispatch(httpCreators.request({
      id: USER_REQUEST_ID_LIST.DELETE_USER,
      sagaHandler: userSagas.deleteUserHandler as any,
      callbackParams: resetReload,
      service: UserService.deleteUser,
      serviceParams: merchantUserId
    }));
  };

  const dispatchModal = (merchantUserId: string) => {
    dispatch(generalCreators.setGeneralState(GENERAL_STATE_KEY.MODAL, {
      visible: true,
      onOk: () => deleteUser(merchantUserId),
      type: MODAL_TYPE.DELETE,
      modalTitle: 'main.user.form.delete.confirm.title',
      modalContent: 'main.user.form.delete.confirm.content'
    }));
  };

  const columns = [
    {
      width: metrics.tableHeader.width180,
      title: <FormattedMessage id={'main.user.manager.name'} />,
      dataIndex: 'managerName',
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }: any) => {
        return (
          <div style={{ padding: '14px 10px' }}>
            <RBHTextInput
              placeholder={'main.user.manager.name.search'}
              data-testid={'search-manager-input'}
              id='search-manager-input'
              style={styleInput.inputTableSearch}
              value={selectedKeys[0]}
              onChange={(e: any) => {
                setSelectedKeys(e.target.value ? [e.target.value] : []);
              }}
              onPressEnter={confirm}
              onBlur={confirm}
            />
          </div>
        );
      },
      filterIcon: (filtered: any) => (
        <SearchOutlined data-testid={'search-manager-icon'} id='search-manager-icon' translate={null} style={{ color: filtered ? Colors.lightPurple : undefined }} />
      ),
      onFilter: (value: any, record: any) => {
        return record.managerName.toString().toLowerCase().includes(value.toLowerCase());
      },
      onFilterDropdownVisibleChange: (visible: boolean) => {
        if (visible) {
          setTimeout(() => {
            const inputEl = document.getElementById('search-input');
            if (inputEl) {
              (inputEl as HTMLInputElement).select();
            }
          });
        }
      },
      render: (text: string) => {
        return <RBHBox style={{ maxWidth: metrics.tableHeader.width180, fontFamily: Fonts.type.KanitMedium }}>{text}</RBHBox>;
      }
    },
    {
      width: metrics.tableHeader.width85,
      title: <FormattedMessage id={'main.user.manager.mobilenumber'} />,
      dataIndex: 'merchantUserMobileNo',
      render: (value: any, all: any, index: number) => {
        const phoneId = 'id-phone-' + index;
        return (
          <RBHBox style={{ width: metrics.tableHeader.width85 }} id={phoneId}>
            {value}
          </RBHBox>
        );
      }
    },
    {
      width: metrics.tableHeader.width120,
      title: <FormattedMessage id={'main.user.manager.email'} />,
      dataIndex: 'merchantUserEmail',
      render: (value: any, all: any, index: number) => {
        const shopNameId = 'id-email-' + index;
        return (
          <RBHBox style={{ width: metrics.tableHeader.width120 }} id={shopNameId}>
            {value}
          </RBHBox>
        );
      }
    },
    {
      width: metrics.tableHeader.width180,
      title: <FormattedMessage id={'main.user.manager.branch'} />,
      dataIndex: ['shop', 'shopName'],
      render: (value: any, all: any, index: number) => {
        const shopNameId = 'id-shopName-' + index;
        return (
          <RBHBox style={{ width: metrics.tableHeader.width180 }} id={shopNameId}>
            {value}
          </RBHBox>
        );
      }
    },
    {
      width: metrics.tableHeader.width100,
      title: <FormattedMessage id={'main.user.status'} />,
      dataIndex: 'merchantUserStatus',
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => {
        return (
          <Menu style={styles.menu} selectable={false}>
            {
              userStatusCode.map((code: string) => {
                return (
                  <Menu.Item
                    key={code}
                    id={code}
                    style={selectedKeys[0] === code ? styles.selectedMenuItem : {}}
                    onClick={() => onMenuItemClick(setSelectedKeys, selectedKeys, confirm, clearFilters, code)}
                  >
                    {MainHelper.getMasterDataByCode(code)}
                  </Menu.Item>
                );
              })
            }
          </Menu>
        );
      },
      filterIcon: (filtered: any) => (
        <FilterFilled id='filter-status-icon' translate={null} style={{ color: filtered ? Colors.lightPurple : undefined }} />
      ),
      onFilter: (value: boolean, record: any) => {
        return record.merchantUserStatus === value;
      },
      render: (value: string, all: any, index: number) => {
        const indexId = 'id-user-status-' + index;
        return <span style={{ width: metrics.tableHeader.width100 }} id={indexId}>{MainHelper.getMasterDataByCode(value)}</span>;
      }
    },
    {
      width: metrics.tableHeader.width30,
      title: <FormattedMessage id={'main.edit'} />,
      align: 'center',
      render: (value: any, all: any, index: number) => {
        const indexId = 'id-edit-' + index;
        const indexIdIcon = 'id-edit-icon-' + index;
        return (<RBHBox style={{ width: metrics.tableHeader.width30 }} id={indexId}>
          <EditOutlined onClick={() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            dispatch(userCreators.setUserState(USER_STATE_KEY.BRANCH_FILTER_DEFAULT_ID, undefined));
            dispatch(userCreators.setUserState(USER_STATE_KEY.BRANCH_FILTER_LIST, undefined));
            setEdit(all);
          }} data-testid={indexIdIcon} id={indexIdIcon} translate={null} style={styleImage.iconTable} />
        </RBHBox>);
      },
    },
    {
      width: metrics.tableHeader.width30,
      title: <FormattedMessage id={'main.delete'} />,
      align: 'center',
      dataIndex: 'merchantUserId',
      render: (value: any, all: any, index: number) => {
        const indexId = 'id-delete-' + index;
        const indexIdIcon = 'id-delete-Icon-' + index;
        return (<RBHBox style={{ width: metrics.tableHeader.width30 }} id={indexId}>
          <DeleteOutlined data-testid={indexIdIcon} id={indexIdIcon} onClick={() => dispatchModal(value)} translate={null} style={styleImage.iconTable} />
        </RBHBox>);
      },
    }
  ];

  const getBreadcrumb = () => {
    return (
      <Breadcrumb style={styleText.breadCrumb}>
        <Breadcrumb.Item>
          <a id='breakcrumb-homepage' style={styleText.breadCrumbItemLink} onClick={() => { history.push('/main'); }}>
            <FormattedMessage id={'main.main'} />
          </a>
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <span id='breakcrumb-user-landing' style={styleText.breadCrumbItem}>
            <FormattedMessage id={'main.user.usermanagement'} />
          </span>
        </Breadcrumb.Item>
      </Breadcrumb>
    );
  };

  const getEditBreadcrumbRender = () => {
    return (
      <RBHBox>
        <Breadcrumb style={styleText.breadCrumb}>
          <Breadcrumb.Item>
            <a id='id-editbranch-breadcrumb-home' style={styleText.breadCrumbItemLink} onClick={() =>
              dispatch(generalCreators.setGeneralState(GENERAL_STATE_KEY.MODAL, { visible: true, onOk: pushToMain, type: MODAL_TYPE.CONFIRM_EDIT }))}>
              <FormattedMessage id={'main.main'} />
            </a>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <a data-testid={'id-editbranch-breadcrumb-branchmanagement'} id='id-editbranch-breadcrumb-branchmanagement' style={styleText.breadCrumbItemLink} onClick={() =>
              dispatch(generalCreators.setGeneralState(GENERAL_STATE_KEY.MODAL, { visible: true, onOk: pushToUser, type: MODAL_TYPE.CONFIRM_EDIT }))}>
              <FormattedMessage id={'main.user.usermanagement'} />
            </a>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <span id='id-editbranch-breadcrumb-editbranch-invisible' style={styleText.breadCrumbItem}>
              <FormattedMessage id={'main.user.edit'} />
            </span>
          </Breadcrumb.Item>
        </Breadcrumb>
      </RBHBox>
    );
  };

  const getTable = () => {
    return (
      <div style={styles.tableWrapper}>
        <RBHTable
          style={{ overflow: 'auto' }}
          columns={columns}
          dataSource={data}
          rowClassName={(record: any) => {
            if (record.merchantUserStatus === userStatusCode[1]) {
              return 'disabled';
            }

            return '';
          }}
          pagination={{
            showSizeChanger: false,
            pageSize: DEFAULT_PAGE_SIZE,
            position: ['bottomLeft'],
            hideOnSinglePage: true,
            showTotal: (total: number, range: number[]) => {
              return (
                <RBHText style={styleText.textPaginationShowTotal}>
                  <FormattedMessage id={'main.showing'} />
                  <span> {range[0] + '-' + range[1]} </span>
                  <FormattedMessage id={'main.of'} />
                  <span> {total}</span>
                </RBHText>
              );
            }
          }}
        >
        </RBHTable>
      </div>
    );
  };

  const AddUserButton = () => {
    return (
      <RBHButton
        id='add-user-button'
        style={styleButton.buttonAdd}
        text={'main.user.add'}
        type='primary'
        icon={<TeamOutlined translate={null} />}
        onClick={addUser}>
      </RBHButton>
    );
  };

  const getEmpty = () => {
    return (
      <div style={styles.emptyWrapper}>
        <RBHEmpty textTitle={'main.user.empty.header'} textContent={'main.user.empty.content'}>
          <AddUserButton></AddUserButton>
        </RBHEmpty>
      </div>
    );
  };

  const getContent = () => {
    if (loading) {
      return null;
    }

    const isError = data === undefined;
    const isEmpty = !!(data && data.length === 0);

    return (
      <RBHBox style={styles.defaultContent}>
        <RBHBox style={styles.header}>
          <RBHText data-testid={'userManagement'} text={'main.user.usermanagement'} style={styleText.headerText} />
          {isEmpty || isError ? null : <AddUserButton />}
        </RBHBox>
        {isError ? <RBHError onOk={() => resetReload()} /> : isEmpty ? getEmpty() : getTable()}
      </RBHBox>
    );
  };

  const getEditContent = () => {
    return (
      <RBHBox style={{minWidth: 'fit-content'}}>
        <RBHBox style={styles.header}>
          <RBHText data-testid={'editUserManagement'} id='id-text-edit-user' style={styleText.headerText} text={'main.user.edit'} />
        </RBHBox>
        <RBHBox style={styles.editWrapper}>
          <FormAddEdit editData={edit} onSuccessCallback={pushToUser} onCancel={() =>
            dispatch(generalCreators.setGeneralState(GENERAL_STATE_KEY.MODAL, { visible: true, onOk: pushToUser, type: MODAL_TYPE.CONFIRM_EDIT }))} />
        </RBHBox>
      </RBHBox>
    );
  };

  return (
    <div>
      {edit ? getEditBreadcrumbRender() : getBreadcrumb()}
      {edit ? getEditContent() : getContent()}
    </div>
  );
};

type Props = {

};
